class ProductMedia extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['items', 'item', 'button']
  }

  setupListeners () {
    super.setupListeners()

    this.config.elements.mediaImages.forEach(mediaImageEl => {
      this.pinchZoom(mediaImageEl)
    })

    if (this.els?.button?.exists) {
      this.els.button.elements.forEach(button => {
        this.listeners.add(button, 'click', this.toggleDesktopZoom.bind(this))
      })
    }

    if (this.els?.thumbnail?.exists) {
      this.els.thumbnail.elements.forEach(thumbnail => {
        this.listeners.add(thumbnail, 'click', this.onThumbnailClick.bind(this))
      })
    }

    this.observeItems = this.observeItems.bind(this)
    this.observeItems()
  }

  observeItems () {
    if (!this.els.thumbnail.exists) return

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: document.body.classList.contains(
        this.config.classes.desktopZoomClass,
      )
        ? 0.2
        : 0.6,
    }

    function observerCallback (entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const thumbnails = document.querySelectorAll(
            '[data-product-media-el="thumbnail"]',
          )
          const navItem = Array.from(thumbnails).find(
            el => el.dataset.index === entry.target.dataset.index,
          )

          if (navItem) {
            navItem.setAttribute('aria-current', true)
            thumbnails.forEach(item => {
              if (item !== navItem) {
                item.setAttribute('aria-current', false)
              }
            })
          }
        }
      })
    }

    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(observerCallback, observerOptions)

    this.els.item.elements.forEach(item => observer.observe(item))
  }

  onThumbnailClick (e) {
    const targetIndex = e.currentTarget.dataset.index
    const mediaContainer = this.els.items.element
    const targetMedia = document.querySelector(
      `.prd-Media_Item[data-index="${targetIndex}"]`,
    )

    if (mediaContainer && targetMedia) {
      mediaContainer.scrollTo({
        top: targetMedia.offsetTop - mediaContainer.offsetTop,
        behavior: 'smooth',
      })
    }
  }

  pinchZoom (imageElement) {
    this.imageElementScale = 1
    this.isScaled = false

    this.singleTouchX = 0
    this.singleTouchY = 0

    const start = {}

    // Calculate distance between two fingers
    const distance = event => {
      return Math.hypot(
        event.touches[0].pageX - event.touches[1].pageX,
        event.touches[0].pageY - event.touches[1].pageY,
      )
    }

    document.body.addEventListener(
      'touchstart',
      event => {
        if (event.target === imageElement) {
          if (event.touches.length === 2) {
            event.preventDefault() // Prevent page scroll

            // Calculate where the fingers have started on the X and Y axis
            start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2
            start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2
            start.distance = distance(event)

            document.body.classList.add(
              this.config.classes.pinchZoomActiveClass,
            )

            const initialTransformValue = document.querySelector(
              this.config.matches.mediaSlideActive,
            ).style.transform
            document.documentElement.style.setProperty(
              '--initial-transform',
              initialTransformValue,
            )
          }
        }
      },
      { passive: true },
    )

    imageElement.addEventListener(
      'touchmove',
      event => {
        if (event.touches.length === 2) {
          event.preventDefault() // Prevent page scroll

          this.isScaled = true

          // Safari provides event.scale as two fingers move on the screen
          // For other browsers just calculate the scale manually
          let scale
          if (event.scale) {
            scale = event.scale
          } else {
            const deltaDistance = distance(event)
            scale = deltaDistance / start.distance
          }
          this.imageElementScale = Math.min(Math.max(1, scale), 4)

          // Calculate how much the fingers have moved on the X and Y axis
          this.deltaX =
            ((event.touches[0].pageX + event.touches[1].pageX) / 2 - start.x) *
            2 // x2 for accelarated movement
          this.deltaY =
            ((event.touches[0].pageY + event.touches[1].pageY) / 2 - start.y) *
            2 // x2 for accelarated movement

          // Transform the image to make it grow and move with fingers
          const transform = `translateX(${this.deltaX}px) translateY(${this.deltaY}px) scale(${this.imageElementScale})`
          imageElement.style.transform = transform
          imageElement.style.WebkitTransform = transform
        }

        if (event.touches.length === 1 && this.isScaled === true) {
          event.preventDefault() // Prevent page scroll

          const movementX = event.touches[0].pageX - this.singleTouchX
          const movementY = event.touches[0].pageY - this.singleTouchY

          const newX = this.deltaX + movementX
          const newY = this.deltaY + movementY

          const transform = `translateX(${newX}px) translateY(${newY}px) scale(${this.imageElementScale})`
          imageElement.style.transform = transform
          imageElement.style.WebkitTransform = transform
        }
      },
      { passive: true },
    )

    imageElement.addEventListener(
      'touchend',
      event => {
        if (event.touches.length === 1) {
          this.singleTouchX = event.touches[0].pageX
          this.singleTouchY = event.touches[0].pageY
        }

        if (event.touches.length === 0) {
          // Reset image to it's original format
          imageElement.style.transform = ''
          imageElement.style.WebkitTransform = ''

          document.body.classList.remove(
            this.config.classes.pinchZoomActiveClass,
          )

          this.isScaled = false
        }
      },
      { passive: true },
    )
  }

  toggleDesktopZoom (e) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    this.currentTarget = e.currentTarget

    document.body.classList.toggle(this.config.classes.desktopZoomClass)
    document.body.classList.toggle(this.config.classes.overflowClass)

    const imageElements = document.querySelectorAll(
      this.config.matches.originalImages,
    )

    if (imageElements !== null) {
      // Swap images to master image
      imageElements.forEach(imageElement => {
        imageElement.src = imageElement.closest(
          this.config.matches.media,
        ).dataset.masterSrc
        imageElement.srcset = ''
        imageElement.classList.add(this.config.classes.masterImageClass)
      })
    }

    this.observeItems()
  }

  get config () {
    return {
      matches: {
        media: '.pz-Media',
        mediaSlideActive: '.prd-Media_Item-active',
        mediaImages:
          '.prd-Media_Item .rsp-Image_Image:not(.rsp-Image_Image-master)',
        originalImages:
          '.prd-Media_Items .prd-Media_Item .rsp-Image_Image:not(.rsp-Image_Image-master)',
      },
      classes: {
        pinchZoomActiveClass: 'lyt-Site-pinchZoomActive',
        mediaSlideActiveClass: 'prd-Media_Item-active',
        desktopZoomClass: 'lyt-Site-desktopZoomActive',
        masterImageClass: 'rsp-Image_Image-master',
        overflowClass: 'util-SiteOverflowed',
      },
      elements: {
        mediaImages: document.querySelectorAll(
          '.pz-Media .rsp-Image_Image:not(.pz-Image)',
        ),
      },
    }
  }
}

if (!customElements.get('product-media')) {
  customElements.define('product-media', ProductMedia)
}
